import { useState, useEffect, useCallback, useContext, createContext } from "react";
import PropTypes from "prop-types";
import UserContext from "./UserContext";
import { getPendingConsentTypes, fetchFooterLinks } from "../services/consentService";
import ConsentDocumentModal from "../components/consent/ConsentDocumentModal.jsx";

export const ConsentContext = createContext({ footerLinks: [] });

export function ConsentProvider({ children }) {
  const userContext = useContext(UserContext);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentStatus, setConsentStatus] = useState(null);
  const [currentConsentType, setCurrentConsentType] = useState(null);
  const [footerLinks, setFooterLinks] = useState([]);

  const checkConsent = useCallback(() => {
    if (!userContext?.user) return;

    const status = userContext.user.consent_statuses;
    const pending = getPendingConsentTypes(status);

    if (pending.length > 0) {
      setConsentStatus(status);
      setCurrentConsentType(pending[0]);
      setShowConsentModal(true);
    } else {
      setShowConsentModal(false);
    }
  }, [userContext?.user]);

  useEffect(() => {
    checkConsent();
  }, [checkConsent]);

  useEffect(() => {
    fetchFooterLinks().then(setFooterLinks);
  }, []);

  const handleAccept = () => {
    setShowConsentModal(false);
    // Refresh user data to get updated consent status
    if (userContext?.refreshUser) {
      userContext.refreshUser();
    }
  };

  const handleDecline = () => {
    // When user declines, we should log them out
    // This is handled by the caller or can be customized
    setShowConsentModal(false);
    // Redirect to logout or show a message that they must accept to continue
    window.location.href = "/login";
  };

  return (
    <ConsentContext.Provider value={{ footerLinks }}>
      {children}
      <ConsentDocumentModal
        open={showConsentModal}
        consentStatus={consentStatus}
        documentType={currentConsentType}
        onAccept={handleAccept}
        onDecline={handleDecline}
      />
    </ConsentContext.Provider>
  );
}

ConsentProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ConsentProvider;
